import React, { useEffect, useState } from 'react';
import { Loan, RetentionOutcome } from '../types';
import { Button } from './ui/Button';
import { Clock, RefreshCw, ArrowLeft, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ClaimedLoanViewProps {
  loan: Loan;
  onRelease: (loanId: string) => void;
  onOpenLoan: (loan: Loan) => void;
  onAction: (loanId: string, outcome: RetentionOutcome, notes: string) => void;
}
export function ClaimedLoanView({
  loan,
  onRelease,
  onOpenLoan,
  onAction
}: ClaimedLoanViewProps) {
  const [tick, setTick] = useState(0);
  const [selectedOutcome, setSelectedOutcome] = useState<RetentionOutcome>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const getRemainingTime = (claimedAt: string) => {
    const claimTime = new Date(claimedAt).getTime();
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const remainingMs = Math.max(0, claimTime + oneHour - now);
    const minutes = Math.floor(remainingMs / (1000 * 60));
    const seconds = Math.floor(remainingMs % (1000 * 60) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  };
  const handleOutcomeSelect = (outcome: RetentionOutcome) => {
    setSelectedOutcome(outcome);
  };
  const handleConfirm = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    onAction(loan.id, selectedOutcome, notes);
    setIsSubmitting(false);
    setSelectedOutcome(null);
    setNotes('');
  };
  const handleCancel = () => {
    setSelectedOutcome(null);
    setNotes('');
  };
  const getOutcomeColor = (outcome: RetentionOutcome) => {
    switch (outcome) {
      case 'Revived - New File':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'Revived - Previous Stage':
        return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };
  const detailFields = [{
    label: 'Loan Amount',
    value: formatCurrency(loan.amount)
  }, {
    label: 'Retention Date',
    value: formatDate(loan.dateEntered)
  }, {
    label: 'Current Owner',
    value: loan.loanOfficer
  }, {
    label: 'Account',
    value: loan.borrowerPhone
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -10
  }} className="space-y-6" data-id="element-1">
      {/* Loan Detail Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-id="element-2">
        <div className="p-7" data-id="element-3">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-7" data-id="element-4">
            <div className="flex-1" data-id="element-5">
              <span className="text-xs font-bold text-accent-600 uppercase tracking-[0.15em] mb-2 block" data-id="element-6">
                Current Claimed Loan
              </span>
              <div className="flex flex-wrap items-center gap-3" data-id="element-7">
                <button onClick={() => onOpenLoan(loan)} className="text-3xl font-bold text-slate-900 hover:text-accent-600 transition-colors text-left tracking-tight underline decoration-slate-200 hover:decoration-accent-400 underline-offset-[6px] decoration-2" data-id="element-8">
                  {loan.borrowerName} - {loan.loanNumber}
                </button>
                {loan.claimedAt && <div className="flex items-center gap-2 text-sm font-semibold text-accent-600 bg-accent-50 px-3 py-1.5 rounded-lg border border-accent-100" data-id="element-9">
                    <Clock className="h-4 w-4" data-id="element-10" />
                    Time Remaining: {getRemainingTime(loan.claimedAt)}
                  </div>}
              </div>
            </div>
            <Button variant="primary" className="bg-accent-500 hover:bg-accent-600 text-white border-transparent shadow-sm shrink-0" onClick={() => onRelease(loan.id)} data-id="element-11">
              Release Loan
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-12">
            {detailFields.map(field => <div key={field.label} className="bg-accent-50/40 rounded-lg p-4 border-l-4 border-l-accent-500" data-id="element-13">
                <span className="text-[10px] font-bold text-accent-600 uppercase tracking-[0.15em] block mb-1.5" data-id="element-14">
                  {field.label}
                </span>
                <span className="text-base font-semibold text-slate-900" data-id="element-15">
                  {field.value}
                </span>
              </div>)}
            <div className="md:col-span-2 bg-accent-50/40 rounded-lg p-4 border-l-4 border-l-accent-500" data-id="element-16">
              <span className="text-[10px] font-bold text-accent-600 uppercase tracking-[0.15em] block mb-1.5" data-id="element-17">
                Reason for Retention
              </span>
              <span className="text-base font-semibold text-slate-900" data-id="element-18">
                {loan.retentionComment}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 px-7 py-3 border-t border-slate-100 text-xs text-slate-500" data-id="element-19">
          Retention loan claimed. Release it when you are ready to return it
          behind the rest of the line.
        </div>
      </div>

      {/* Take Action Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-id="element-20">
        <div className="px-7 pt-6 pb-4" data-id="element-21">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-[0.15em]" data-id="element-22">
            Take Action
          </h3>
          <p className="text-xs text-slate-500 mt-1" data-id="element-23">
            Select an outcome to resolve this retention case.
          </p>
        </div>
        <div className="px-7 pb-7" data-id="element-24">
          <AnimatePresence mode="wait" data-id="element-25">
            {!selectedOutcome ? <motion.div key="select" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-26">
                <button onClick={() => handleOutcomeSelect('Revived - New File')} className="group flex items-center gap-4 p-5 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition-all text-left" data-id="element-27">
                  <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-28">
                    <RefreshCw className="h-5 w-5" data-id="element-29" />
                  </div>
                  <div data-id="element-30">
                    <h4 className="font-bold text-slate-900 mb-0.5" data-id="element-31">
                      Revive - New File
                    </h4>
                    <p className="text-xs text-slate-500" data-id="element-32">
                      Restart the process from App Started.
                    </p>
                  </div>
                </button>

                <button onClick={() => handleOutcomeSelect('Revived - Previous Stage')} className="group flex items-center gap-4 p-5 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all text-left" data-id="element-33">
                  <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-34">
                    <ArrowLeft className="h-5 w-5" data-id="element-35" />
                  </div>
                  <div data-id="element-36">
                    <h4 className="font-bold text-slate-900 mb-0.5" data-id="element-37">
                      Revive - Previous Stage
                    </h4>
                    <p className="text-xs text-slate-500" data-id="element-38">
                      Return file to the previous processing stage.
                    </p>
                  </div>
                </button>
              </motion.div> : <motion.div key="confirm" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-5" data-id="element-39">
                <div className={`p-4 rounded-xl border flex items-center gap-3 ${getOutcomeColor(selectedOutcome)}`} data-id="element-40">
                  <AlertTriangle className="h-5 w-5 shrink-0" data-id="element-41" />
                  <div data-id="element-42">
                    <p className="text-sm font-semibold" data-id="element-43">
                      You are about to:{' '}
                      <span className="font-bold" data-id="element-44">{selectedOutcome}</span>
                    </p>
                    <p className="text-xs opacity-90" data-id="element-45">
                      This action will be recorded in the loan history.
                    </p>
                  </div>
                </div>

                <div data-id="element-46">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-47">
                    Resolution Notes{' '}
                    <span className="text-slate-400 font-normal" data-id="element-48">
                      (Optional)
                    </span>
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any context about this decision..." className="w-full min-h-[100px] p-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-accent-500 focus:border-transparent text-sm resize-none" data-id="element-49" />
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-50">
                  <Button variant="ghost" onClick={handleCancel} disabled={isSubmitting} data-id="element-51">
                    Back
                  </Button>
                  <Button variant="primary" className="bg-accent-500 hover:bg-accent-600 text-white border-transparent" onClick={handleConfirm} isLoading={isSubmitting} data-id="element-52">
                    Confirm & Resolve
                  </Button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}